import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useSelector } from "@/redux/store";
import { appSelector } from "@/redux/selectors/app.selector";
import { Building, Check, ChevronLeft } from "lucide-react";
import { setInternalOrgOverride } from "@/redux/actions/app.actions";
import { Role } from "@/enums/role";
import { useGetAllOrganizationsQuery } from "@/hooks/queries/internal";
import type { OrganizationIdAndName } from "@rebuild/shared/generated/models/organizationIdAndName";

/**
 * Top row of the NavUserDropdown. For internal Rebuild users this is a submenu
 * that lets them switch the active organization override; for everyone else it
 * is a static row showing the current org name.
 */
export function DropDownMenuOrgOption() {
  const { user } = useAuth();
  const orgName = useSelector(appSelector.organizationName);
  const orgId = useSelector(appSelector.organizationId);
  const isInternal = user?.role === Role.INTERNAL;

  const { data: organizations, isLoading: isLoadingOrgs } = useGetAllOrganizationsQuery(isInternal);

  const handleOrganizationSwitch = (org: OrganizationIdAndName) => {
    setInternalOrgOverride(org.organizationId, org.organizationName);
    window.location.reload();
  };

  if (!isInternal) {
    return (
      <DropdownMenuItem className="flex w-full items-center gap-2 !bg-white p-2 hover:!text-black">
        <Building className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 truncate text-left">{orgName}</p>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        suppressChevron
        className="flex w-full items-center gap-2 !bg-white p-2 hover:!bg-gray-100 hover:!text-black"
      >
        <ChevronLeft className="h-4 w-4 shrink-0" />
        <Building className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 truncate text-left">{orgName}</p>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-80 overflow-y-auto !border-gray-200 !bg-white !text-black">
        {isLoadingOrgs ? (
          <DropdownMenuItem disabled className="!bg-white !text-gray-500">
            Loading...
          </DropdownMenuItem>
        ) : (
          (organizations ?? []).map((org) => (
            <DropdownMenuItem
              key={org.organizationId}
              onClick={() => handleOrganizationSwitch(org)}
              className="cursor-pointer hover:!bg-gray-100 hover:!text-black"
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="truncate">{org.organizationName}</span>
                {org.organizationId === orgId && <Check className="h-4 w-4 flex-shrink-0 text-green-600" />}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
